<script>
import { mapActions, mapState } from 'vuex';
import ValidatorMixin from '../../mixins/validatorMixin';
import ItineraryDayProgramImageList from './ItineraryDayProgramImageList';
import TwImageUpload from '../TwImageUpload';

export default {
  name: 'ItineraryEditGeneralInfo',
  components: {
    TwImageUpload,
    ItineraryDayProgramImageList,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: [Boolean, Object],
      default: false,
    },
    item: {
      type: Object,
      required: true,
    },
    productTypes: {
      type: Array,
      default: () => [],
    },
    selectedDomain: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      name: null,
      minimumAge: null,
      localName: null,
      bannerTeaser: null,
      localBannerTeaser: null,
      isExtension: null,
      difficultyLevel: null,
      valid: true,
      unique: true,
    };
  },
  computed: {
    ...mapState({
      bannerImages: state => state.itinerary.bannerImages.items,
      mapImages: state => state.itinerary.mapImages.items,
    }),
    domainName: {
      get() { return this.localName; },
      set(val) { this.localName = val; },
    },
    domainBannerTeaser: {
      get() { return this.localBannerTeaser; },
      set(val) { this.localBannerTeaser = val; },
    },
  },
  methods: {
    ...mapActions({
      uploadBannerImage: 'itinerary/uploadBannerImage',
      getItineraryBannerImages: 'itinerary/getItineraryBannerImages',
      updateItineraryBannerImages: 'itinerary/updateItineraryBannerImages',
      deleteItineraryBannerImage: 'itinerary/deleteItineraryBannerImage',
      uploadMapImage: 'itinerary/uploadMapImage',
      getItineraryMapImages: 'itinerary/getItineraryMapImages',
      updateItineraryMapImages: 'itinerary/updateItineraryMapImages',
      deleteItineraryMapImage: 'itinerary/deleteItineraryMapImage',
    }),
    update() {
      this.$emit('update', {
        ...this.item,
        name: this.name,
        bannerTeaser: this.bannerTeaser,
        isExtension: this.isExtension,
        difficultyLevel: this.difficultyLevel || null,
        minimumAge: this.minimumAge,
      });
      if (this.selectedDomain) {
        this.$emit('updateDomain', {
          ...this.selectedDomain,
          name: this.domainName,
          bannerTeaser: this.localBannerTeaser,
          minimumAge: this.minimumAge,
        });
      }
    },
    setLocalValues(item) {
      this.name = item.name;
      this.bannerTeaser = item.bannerTeaser;
      this.isExtension = item.isExtension;
      this.difficultyLevel = item.difficultyLevel;
      this.minimumAge = item.minimumAge;
    },
    upload(event) {
      this.uploadBannerImage({
        itineraryId: this.$route.params.id,
        images: [event.file],
      }).then(() => {
        this.saveSuccess = true;
        this.snackbar = true;
      }).catch(() => {
        this.saveSuccess = false;
        this.error = true;
        this.snackbar = true;
      });
    },
    move(event) {
      const orderedFileIds = event.map(item => item.id);
      this.updateItineraryBannerImages({
        itineraryId: this.$route.params.id,
        fileIds: orderedFileIds,
      });
    },
    remove(event) {
      this.deleteItineraryBannerImage({
        itineraryId: this.$route.params.id,
        image: event,
      });
    },
    uploadMap(event) {
      this.uploadMapImage({
        itineraryId: this.$route.params.id,
        images: [event.file],
      });
    },
    moveMap(event) {
      const orderedFileIds = event.map(item => item.id);
      this.updateItineraryMapImages({
        itineraryId: this.$route.params.id,
        fileIds: orderedFileIds,
      });
    },
    removeMap(event) {
      this.deleteItineraryMapImage({
        itineraryId: this.$route.params.id,
        image: event,
      });
    },
  },
  watch: {
    item(item) {
      this.setLocalValues(item);
    },
    error(value) {
      if (value) {
        this.valid = false;
        this.unique = 'The name must be unique';
      }
    },
    name() {
      this.unique = true;
    },
    selectedDomain(val) {
      this.domainName = val ? val.name : null;
      this.domainBannerTeaser = val ? val.bannerTeaser : null;
    },
  },
  created() {
    this.setLocalValues(this.item);
    if (this.selectedDomain) {
      this.domainName = this.selectedDomain.name;
      this.domainBannerTeaser = this.selectedDomain.bannerTeaser;
    }
    this.getItineraryBannerImages(this.$route.params.id);
    this.getItineraryMapImages(this.$route.params.id);
  },
  mixins: [ValidatorMixin],
};
</script>

<template>
  <v-container class="ma-0 fluid">
    <v-card>
      <v-card-title class="pb-0">
        <div class="headline">{{ $t('itinerary.edit.general.title') }}</div>
      </v-card-title>
      <v-card-text>
        <v-form v-model="valid">
          <v-flex pa-4>
            <v-text-field
              v-model="name"
              clearable
              :rules="[rules.required, unique]"
              :label="$t('itinerary.create.name')"
              :loading="loading"
              class="my-3"
            />
            <v-text-field
              v-if="selectedDomain"
              v-model="domainName"
              clearable
              :rules="[rules.required, unique]"
              :label="$t('itinerary.create.localName')"
              :loading="loading"
              class="my-3"
            />
            <v-select
              :value="item.productTypeId"
              item-text="name"
              item-value="id"
              :label="$t('itinerary.create.productType')"
              :items="productTypes"
              :loading="loading"
              class="my-3"
              disabled
            />
            <v-flex xs12>
              <v-text-field
                v-model="minimumAge"
                :value="item.minimumAge"
                label="Minimum age"
              />
            </v-flex>
            <v-flex xs12 sm6 class="py-2">
              <p class="edit-label">{{ $t('itinerary.edit.general.difficulty.label') }}</p>
              <v-btn-toggle v-model="difficultyLevel">
                <v-btn
                  v-for="n in 6"
                  :key="`difficulty_level-${n}`"
                  :value="n"
                  flat
                  class="px-3"
                >
                  {{ $t('itinerary.edit.general.difficulty.levels', {choice: n}) }}
                </v-btn>
              </v-btn-toggle>
            </v-flex>
            <v-text-field
              v-model="bannerTeaser"
              clearable
              :label="$t('itinerary.create.bannerTeaser')"
              :loading="loading"
              class="my-3"
            />
            <v-text-field
              v-if="selectedDomain"
              v-model="domainBannerTeaser"
              clearable
              :label="$t('itinerary.create.localBannerTeaser')"
              :loading="loading"
              class="my-3"
            />
            <tw-image-upload
              :images="bannerImages"
              button-title="UPLOAD BANNER IMAGE"
              @upload="upload"
            />
            <itinerary-day-program-image-list
              :images="bannerImages"
              @remove="remove"
              @move="move"
            />
            <tw-image-upload
              :images="mapImages"
              button-title="UPLOAD MAP IMAGE"
              @upload="uploadMap"
            />
            <itinerary-day-program-image-list
              :images="mapImages"
              @remove="removeMap"
              @move="moveMap"
            />
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-checkbox
                  v-model="isExtension"
                  v-on="on"
                  :disabled="loading"
                  :label="$t('itinerary.create.extension')"/>
              </template>
              <span>{{ $t('itinerary.create.tooltips.extension') }}</span>
            </v-tooltip>
          </v-flex>
          <v-divider/>
          <v-spacer/>
          <v-layout justify-end>
            <v-btn
              color="primary success"
              :disabled="!valid"
              :loading="loading"
              @click="update()"
            >
              Save
            </v-btn>
          </v-layout>
        </v-form>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<style lang="css">
  .edit-label {
    font-size: 11px;
    opacity: .8;
  }
</style>



// WEBPACK FOOTER //
// src/components/itinerary/ItineraryEditGeneralInfo.vue